import React, { Fragment, useEffect, useState } from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useTheme } from '@mui/material/styles';
import {
	Stack,
	Typography,
	CircularProgress,
	TextField,
	IconButton,
	List,
	ListItem,
	ListItemText,
	ListItemAvatar,
	Avatar,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import moment from 'moment';
import utils from 'components/utils';
import chat from 'services/chat';

const Chat = props => {
	const { type, id, titleChat } = props;
	const theme = useTheme();
	const translate = utils.useI18N();
	const loading = useStoreState(state => state.chat.loading);
	const chats = useStoreState(state => state.chat.chats);
	const loadChat = useStoreActions(dispatch => dispatch.chat.loadChat);
	const loadingSend = useStoreState(state => state.chat.loadingSend);
	const sendChat = useStoreActions(dispatch => dispatch.chat.sendChat);
	const [message, setMessage] = useState('');
	const [openChat, setopenChat] = useState(false);
	const colors = [
		theme.palette.primary.main,
		theme.palette.custom.hover,
		theme.palette.custom.chip,
		theme.palette.custom.orange,
		theme.palette.custom.yellow,
		theme.palette.custom.red,
		theme.palette.custom.green,
		theme.palette.secondary.main
	];
	const colorList = Array.from(new Set(chats.map(c => c.sender))).map((s, index) => {
		return { sender: s, color: index < colors.length - 1 ? colors[index] : colors[0] };
	});
	/* Versione con colori random
    const colorList = Array.from(new Set(chats.map(c => c.sender)))
        .map(s => {return {sender:s,color:Math.random().toString(16).substr(-6)}});
    */

	const getColor = code => {
		const colorItem = colorList.filter(item => item.sender === code);
		if (colorItem.length > 0) return colorItem[0].color;
		//Per la versione coi colori random aggiunger '#'
		else return theme.palette.primary.main;
	};

	useEffect(() => {
		if (openChat) {
			loadChat({ type, id });
		}
	}, [openChat]);

	const handleOpenChat = event => {
		setopenChat(true);
	};

	const handleCloseChat = () => {
		setopenChat(false);
	};

	const sendMessage = _ => {
		if (!message || message.trim() == '') return;
		sendChat({ type: type, id: id, notes: message });
		setMessage('');
	};

	return (
		<Stack direction="column" justifyContent="flex-end" alignItems="center" spacing={0}>
			{/* Icona di apertura chat */}
			<Tooltip title={translate('wishlistSpecial.openChat')}>
				<IconButton onClick={handleOpenChat}>
					<ChatIcon sx={{ color: theme.palette.primary.main }} />
				</IconButton>
			</Tooltip>
			{/* Dialog box */}
			<Dialog open={openChat} onClose={handleCloseChat} sx={{ border: '2px solid black' }}>
				<DialogTitle
					sx={{
						textAlign: 'left',
						padding: '0px !Important',
						borderWidth: '0 0 1px 0',
						borderStyle: 'solid',
						borderColor: theme.palette.custom.greyborder
					}}
				>
					<Typography variant="titleTab">{titleChat}</Typography>
				</DialogTitle>
				<IconButton onClick={handleCloseChat} sx={{ position: 'absolute', right: 8, top: 0 }}>
					<CloseIcon />
				</IconButton>
				{/* Chat */}
				<DialogContent sx={{ padding: '10px', width: { xs: '320px', md: '600px', sm: '600px' } }}>
					<Stack
						direction="column"
						justifyContent="flex-start"
						alignItems="flex-start"
						spacing={1}
						sx={{ width: '100%' }}
					>
						<Stack
							direction="column"
							justifyContent="flex-start"
							alignItems="flex-start"
							spacing={1}
							sx={{ width: '100%', backgroundColor: theme.palette.custom.grey }}
						>
							{loading && <CircularProgress size={24} />}
							{!loading && (
								<List sx={{ width: '100%', overflowY: 'scroll', height: '400px' }}>
									{chats.map((chat, index) => (
										<ListItem key={chat.id} alignItems="flex-start">
											<ListItemAvatar>
												<Avatar
													sx={{
														backgroundColor: getColor(chat.sender),
														width: '36px',
														height: '36px'
													}}
												>
													{chat.senderName.split(' ')[0][0].toUpperCase() +
														chat.senderName.split(' ')[1][0].toUpperCase()}
												</Avatar>
											</ListItemAvatar>
											<ListItemText
												primary={
													<Stack
														direction="column"
														justifyContent="flex-start"
														alignItems="flex-start"
														spacing={0}
														component="div"
													>
														<Typography
															variant="titleTab"
															sx={{
																textTransform: 'capitalize !important',
																marginLeft: '-2px !important'
															}}
														>
															{moment(chat.creationDate).format('LLL')}
														</Typography>
														<Typography
															variant="textTable"
															sx={{
																textTransform: 'capitalize !important',
																fontWeight: '600 !important'
															}}
														>
															{chat.senderName}
														</Typography>
													</Stack>
												}
												secondary={
													<Fragment>
														<Typography
															variant="textTable"
															sx={{
																fontWeight: '600 !important',
																fontStyle: 'italic'
															}}
														>
															{chat.message}
														</Typography>
													</Fragment>
												}
											/>
										</ListItem>
									))}
								</List>
							)}
						</Stack>
						<Stack
							direction="row"
							justifyContent="flex-start"
							alignItems="flex-start"
							spacing={0}
							sx={{ width: '100%' }}
						>
							<TextField
								multiline={false}
								maxRows={2}
								rows={1}
								maxLength={100}
								placeholder={translate('chat.writeYourText')}
								onChange={e => {
									setMessage(e.target.value);
								}}
								onKeyDown={e => {
									if (e.key === 'Enter') {
										sendMessage();
										e.target.value = '';
									}
								}}
								sx={{ width: '90%' }}
							/>
							{loadingSend && <CircularProgress size={24} />}
							{!loadingSend && (
								<Tooltip title={translate('chat.send')}>
									<IconButton onClick={sendMessage} sx={{ marginTop: '-5px' }}>
										<ArrowCircleRightIcon
											sx={{
												color: theme.palette.primary.main,
												width: '50px',
												height: '50px'
											}}
										/>
									</IconButton>
								</Tooltip>
							)}
						</Stack>
					</Stack>
				</DialogContent>
			</Dialog>
		</Stack>
	);
};

export default Chat;
